/**
 * FormSelectField Component
 * Reusable select dropdown with consistent styling
 *
 * Features:
 * - Consistent label and select styling
 * - Built-in error state and messaging
 * - Optional helper text
 * - Required field indicator
 * - Generic option type support
 */

import {
  FORM_GROUP_STYLE,
  INPUT_STYLE,
  LABEL_STYLE,
} from "../../styles/modalStyles";
import { SELECT_CHEVRON_STYLE } from "../../styles/components";

export interface SelectOption<T = string> {
  value: T;
  label: string;
}

export interface FormSelectFieldProps<T = string> {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: SelectOption<T>[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
}

export function FormSelectField<T extends string = string>({
  label,
  value,
  onChange,
  options,
  placeholder,
  required = false,
  disabled = false,
  error,
  helperText,
}: FormSelectFieldProps<T>) {
  return (
    <div style={FORM_GROUP_STYLE}>
      <label style={LABEL_STYLE}>
        {label}
        {required && (
          <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>
        )}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        required={required}
        disabled={disabled}
        style={{
          ...INPUT_STYLE,
          borderColor: error ? "#ef4444" : "#d1d5db",
          backgroundColor: disabled ? "#f9fafb" : "#ffffff",
          cursor: disabled ? "not-allowed" : "pointer",
          ...SELECT_CHEVRON_STYLE,
          paddingRight: "36px",
        }}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={String(option.value)} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <span
          style={{
            display: "block",
            marginTop: "4px",
            fontSize: "12px",
            color: "#ef4444",
          }}
        >
          {error}
        </span>
      )}
      {helperText && !error && (
        <span
          style={{
            display: "block",
            marginTop: "4px",
            fontSize: "12px",
            color: "#6b7280",
          }}
        >
          {helperText}
        </span>
      )}
    </div>
  );
}
